import { cn } from '~/lib/cn';

export type CurrencyCode = 'USD' | 'GBP' | 'EUR';

/** Circular flag badges for the hero balances card (inline SVG — no assets). */
export function CurrencyFlag({ code, className }: { code: CurrencyCode; className?: string }) {
  return (
    <span className={cn('inline-block shrink-0 overflow-hidden rounded-full', className)}>
      {code === 'USD' && <UsFlag />}
      {code === 'GBP' && <GbFlag />}
      {code === 'EUR' && <EuFlag />}
    </span>
  );
}

const svgProps = {
  viewBox: '0 0 40 40',
  width: '100%',
  height: '100%',
  role: 'presentation' as const,
  'aria-hidden': true,
};

function UsFlag() {
  const stripeH = 40 / 13;
  const cantonH = stripeH * 7;
  const cantonW = 17;
  return (
    <svg {...svgProps}>
      <rect width="40" height="40" fill="#fff" />
      {Array.from({ length: 7 }).map((_, i) => (
        <rect key={i} y={i * 2 * stripeH} width="40" height={stripeH} fill="#b22234" />
      ))}
      <rect width={cantonW} height={cantonH} fill="#3c3b6e" />
      {Array.from({ length: 4 }).map((_, row) =>
        Array.from({ length: 5 }).map((_, col) => (
          <circle
            key={`${row}-${col}`}
            cx={2.6 + col * 3}
            cy={2.8 + row * 4.4}
            r="0.7"
            fill="#fff"
          />
        )),
      )}
    </svg>
  );
}

function GbFlag() {
  return (
    <svg {...svgProps}>
      <rect width="40" height="40" fill="#012169" />
      <path d="M0,0 L40,40 M40,0 L0,40" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 L40,40 M40,0 L0,40" stroke="#c8102e" strokeWidth="4" />
      <path d="M20,0 V40 M0,20 H40" stroke="#fff" strokeWidth="13" />
      <path d="M20,0 V40 M0,20 H40" stroke="#c8102e" strokeWidth="7" />
    </svg>
  );
}

function starPath(cx: number, cy: number, outer: number, inner: number) {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? outer : inner;
    pts.push(`${(cx + r * Math.cos(angle)).toFixed(2)},${(cy + r * Math.sin(angle)).toFixed(2)}`);
  }
  return `M${pts.join(' L')} Z`;
}

function EuFlag() {
  const ringR = 12;
  return (
    <svg {...svgProps}>
      <rect width="40" height="40" fill="#003399" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = -Math.PI / 2 + (i * Math.PI) / 6;
        const cx = 20 + ringR * Math.cos(angle);
        const cy = 20 + ringR * Math.sin(angle);
        return <path key={i} d={starPath(cx, cy, 2.3, 0.92)} fill="#ffcc00" />;
      })}
    </svg>
  );
}
